import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'subtle'
  | 'authGoogle'
  | 'authContinue'
  | 'mobileSuggestion'
  | 'mobileComposerIcon'
  | 'mobileComposerSend'
  | 'whiteCta';
type Size = 'small' | 'default' | 'large' | 'icon' | 'sm' | 'lg';

interface MHButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
}

export const MHButton = React.forwardRef<HTMLButtonElement, MHButtonProps>(
  ({ className, asChild = false, variant = 'primary', size = 'default', fullWidth = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    const normalizedSize = size === 'sm' ? 'small' : size === 'lg' ? 'large' : size;

    return (
      <Comp
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-md transition-all duration-motion-md ease-calm active:scale-[0.98]',
          'focus:outline-none focus:ring-2 focus:ring-ring-1 focus:ring-offset-0',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:transform-none',
          variant === 'primary' && 'h-11 border border-transparent bg-accent px-4 text-body font-medium text-white hover:brightness-95',
          variant === 'secondary' && 'h-11 border border-border bg-surface-2 px-4 text-body font-medium text-text-1 hover:bg-surface-1',
          variant === 'subtle' && 'h-11 border border-transparent bg-surface-1 px-4 text-body font-medium text-text-2 hover:bg-surface-2 hover:text-text-1',
          variant === 'authGoogle' && 'h-11 border border-border bg-surface-2 px-4 text-body font-medium text-text-1 hover:bg-surface-1',
          variant === 'authContinue' && 'h-11 border border-transparent bg-accent px-4 text-body font-medium text-white hover:brightness-95 disabled:bg-surface-1 disabled:text-text-3',
          variant === 'ghost' && 'h-10 border border-transparent bg-transparent px-3 text-body font-medium text-text-2 hover:bg-surface-1 hover:text-text-1',
          variant === 'mobileSuggestion' && 'h-11 justify-start rounded-xl border border-border bg-surface-1 px-4 text-body font-medium text-text-1 hover:bg-surface-2',
          variant === 'mobileComposerIcon' && 'h-11 w-11 rounded-md border border-border bg-surface-1 p-0 text-text-2 hover:bg-surface-2 hover:text-text-1',
          variant === 'mobileComposerSend' && 'h-11 w-11 rounded-md border border-transparent bg-accent p-0 text-white hover:brightness-95',
          variant === 'whiteCta' && 'h-11 border border-transparent bg-accent px-4 text-body font-medium text-white hover:brightness-95',
          normalizedSize === 'small' && 'h-10 px-3 text-small',
          normalizedSize === 'default' && 'h-11 px-4 text-body',
          normalizedSize === 'large' && 'h-11 px-6 text-body',
          normalizedSize === 'icon' && 'h-11 w-11 p-0 text-body',
          fullWidth && 'w-full',
          className
        )}
        {...props}
      />
    );
  }
);

MHButton.displayName = 'MHButton';

export type { MHButtonProps };
